import { Typography } from '@community_dev/pixels'
import { Alert, Space } from 'antd'
import type { JSX } from 'react'

import { RoleChanges, SENSITIVE_CAPABILITY_NOTES } from '../roleChanges'

import { RoleDetails } from 'api/capabilities'
import { Capability } from 'api/clients'
import { StyledModal } from 'shared/components/Modal'

type ReviewRoleChangesModalProps = {
  role: RoleDetails
  changes: RoleChanges
  isSaving: boolean
  onCancel: () => void
  onConfirm: () => void
}

function CapabilityNames({ title, capabilities }: { title: string; capabilities: Capability[] }): JSX.Element | null {
  if (capabilities.length === 0) return null

  return (
    <div>
      <Typography variant="caption1">{title}</Typography>
      <ul>
        {capabilities.map((capability) => (
          <li key={capability.name}>{capability.name}</li>
        ))}
      </ul>
    </div>
  )
}

export function ReviewRoleChangesModal({
  role,
  changes,
  isSaving,
  onCancel,
  onConfirm,
}: ReviewRoleChangesModalProps): JSX.Element {
  const seatCount = role.seatMappingCount
  const sensitiveChanges = [
    ...changes.added.map((capability) => ({ capability, verb: 'Adds' })),
    ...changes.removed.map((capability) => ({ capability, verb: 'Removes' })),
  ].filter(({ capability }) => SENSITIVE_CAPABILITY_NOTES[capability.name])

  return (
    <StyledModal
      confirmLoading={isSaving}
      okText="Save role"
      onCancel={onCancel}
      onOk={onConfirm}
      open={true}
      title={`Review changes to ${role.name}`}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          description="Each seat gets the new capabilities at its next sign-in or session refresh. Until then, a session keeps the old capabilities."
          message={`${seatCount} ${seatCount === 1 ? 'seat holds' : 'seats hold'} this role.`}
          showIcon
          type="info"
        />
        {sensitiveChanges.map(({ capability, verb }) => (
          <Alert
            description={SENSITIVE_CAPABILITY_NOTES[capability.name]}
            key={capability.name}
            message={`${verb} ${capability.name}`}
            showIcon
            type="warning"
          />
        ))}
        {changes.displayName && (
          <Typography variant="body2">
            Display name: “{changes.displayName.before}” → “{changes.displayName.after}”
          </Typography>
        )}
        {changes.descriptionChanged && <Typography variant="body2">The description changes.</Typography>}
        <CapabilityNames capabilities={changes.added} title="Added capabilities" />
        <CapabilityNames capabilities={changes.removed} title="Removed capabilities" />
      </Space>
    </StyledModal>
  )
}
